/**
 * Page d'accueil — landing minimaliste, avec sélecteur de zone maritime.
 */

import { el, clear } from '../js/utils.js';
import { t } from '../js/i18n.js';
import { pingSupabase } from '../js/supabase.js';
import { countSightings } from '../js/sightings.js';
import { listActiveZones, zoneName, zoneIcon } from '../js/zones.js';
import { listFeaturedSpecies, displayName } from '../js/species.js';
import { renderHeader } from '../components/header.js';
import { renderFooter } from '../components/footer.js';
import { renderProfileCard } from '../components/profile-card.js';

const ZONE_STORAGE_KEY = 'selected_zone_id';

function getStoredZoneId() {
  return localStorage.getItem(ZONE_STORAGE_KEY) ?? null;
}

function saveZoneId(id) {
  localStorage.setItem(ZONE_STORAGE_KEY, id);
}

export async function renderHome(root) {
  clear(root);

  const { total } = countSightings();
  const profileSlot = total > 0
    ? el('section', { class: 'home-profile container', 'aria-label': t('profile.anonymous') })
    : null;

  // ---- Zone selector ----
  const zonesGrid = el('div', {
    class: 'home-zones-grid',
    role: 'radiogroup',
    'aria-label': t('home.zone_selector.title'),
  },
    el('div', { class: 'zone-card zone-card--skeleton skeleton' }),
    el('div', { class: 'zone-card zone-card--skeleton skeleton' })
  );

  const zonesSection = el('div', { class: 'home-zones' },
    el('p', { class: 'home-zones-label eyebrow' }, t('home.zone_selector.title')),
    zonesGrid
  );

  // ---- Section Découvrir ----
  const discoverGrid = el('div', { class: 'home-discover-grid', id: 'discover-grid' },
    ...Array.from({ length: 5 }, () =>
      el('div', { class: 'discover-card-skeleton skeleton' })
    )
  );

  const discoverSection = el('section', { class: 'home-discover container' },
    el('p', { class: 'eyebrow home-discover-label' }, t('home.discover.title')),
    discoverGrid
  );

  const view = el(
    'main',
    { class: 'home' },
    el(
      'section',
      { class: 'home-hero container' },
      el('p', { class: 'eyebrow home-eyebrow' }, t('home.eyebrow')),
      el(
        'h1',
        { class: 'home-title' },
        t('home.title.pre'),
        el('em', {}, t('home.title.em')),
        '.'
      ),
      el('p', { class: 'home-lead' }, t('home.lead')),
      zonesSection
    ),
    discoverSection,
    profileSlot,
    el(
      'div',
      { class: 'home-footer container' },
      el(
        'span',
        { class: 'home-status' },
        el('span', { class: 'home-status-dot' }),
        el('span', { id: 'supabase-status' }, t('home.status.loading'))
      )
    )
  );

  root.append(renderHeader({ variant: 'over-dark' }), view, renderFooter());

  if (profileSlot) renderProfileCard(profileSlot);

  // ---- State ----
  let selectedZoneId = getStoredZoneId();
  let zonesData = [];

  function selectZone(id) {
    selectedZoneId = id;
    saveZoneId(id);
    zonesGrid.querySelectorAll('.zone-card').forEach(card => {
      const isSelected = card.dataset.zoneId === id;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', String(isSelected));
    });
  }

  function buildZoneCards() {
    clear(zonesGrid);
    zonesData.forEach(zone => {
      const isSelected = zone.id === selectedZoneId;
      const countLabel = t('home.zone_selector.species_count', { count: zone.species_count });
      const card = el('button', {
        type: 'button',
        role: 'radio',
        class: `zone-card${isSelected ? ' is-selected' : ''}`,
        'aria-checked': String(isSelected),
        'aria-label': `${zoneName(zone)}, ${countLabel}`,
        dataset: { zoneId: zone.id },
        onclick: () => {
          selectZone(zone.id);
          window.location.hash = `#/species?zone_id=${zone.id}`;
        },
      },
        el('span', { class: 'zone-card-icon', 'aria-hidden': 'true' }, zoneIcon(zone.slug)),
        el('span', { class: 'zone-card-name' }, zoneName(zone)),
        el('span', { class: 'zone-card-count' }, countLabel)
      );
      zonesGrid.append(card);
    });
  }

  // Navigation clavier dans le radiogroup (flèches + entrée)
  zonesGrid.addEventListener('keydown', e => {
    const cards = [...zonesGrid.querySelectorAll('.zone-card:not(.zone-card--skeleton)')];
    if (!cards.length) return;
    const idx = cards.indexOf(document.activeElement);
    if (idx === -1) return;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      cards[(idx + 1) % cards.length].focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      cards[(idx - 1 + cards.length) % cards.length].focus();
    }
  });

  // ---- Fetch zones + espèces Découvrir en parallèle ----
  const [zonesResult, featuredResult] = await Promise.allSettled([
    listActiveZones(),
    listFeaturedSpecies({ zoneId: selectedZoneId, limit: 5 }),
  ]);

  // Zones
  if (zonesResult.status === 'fulfilled') {
    zonesData = zonesResult.value;
    if (zonesData.length > 0) {
      buildZoneCards();
    } else {
      zonesSection.hidden = true;
    }
  } else {
    console.error('[home] Erreur chargement zones :', zonesResult.reason);
    zonesSection.hidden = true;
  }

  // Découvrir
  if (featuredResult.status === 'fulfilled' && featuredResult.value.length > 0) {
    clear(discoverGrid);
    for (const { species, zone } of featuredResult.value) {
      const name = displayName(species);
      const card = el('a', { href: `#/species/${species.id}`, class: 'discover-card' },
        el('div', { class: 'discover-card-photo' },
          species.primary_image_url
            ? el('img', { src: species.primary_image_url, alt: name, loading: 'lazy' })
            : el('div', { class: 'discover-card-photo-empty' })
        ),
        el('div', { class: 'discover-card-body' },
          el('p', { class: 'discover-card-name' }, name),
          el('p', { class: 'discover-card-zone' }, zoneName(zone))
        )
      );
      discoverGrid.append(card);
    }
  } else {
    discoverSection.hidden = true;
  }

  // ---- Test de connexion Supabase ----
  const status = root.querySelector('#supabase-status');
  const result = await pingSupabase();
  if (result.ok) {
    status.textContent = t('home.status.ok');
  } else {
    status.textContent = t('home.status.offline');
    status.parentElement.querySelector('.home-status-dot').style.background = 'var(--rust)';
  }
}
